/**
 * Browser-side download for public-read URLs.
 *
 * Streams the response body so download progress can be reported, then saves it
 * via a synthetic anchor click. The blob round-trip (rather than a bare
 * <a download>) is what forces a download with a chosen filename for
 * cross-origin S3 URLs, where the `download` attribute is otherwise ignored.
 * No S3 SDK / presigning — the objects are public read.
 *
 * Progress requires a readable Content-Length (S3 sends it on GET; it is
 * CORS-safelisted so no expose-header config is needed). When it is absent,
 * onProgress simply never fires and callers should treat the bar as
 * indeterminate.
 */

export class DownloadError extends Error {
	constructor(message, status) {
		super(message);
		this.name = 'DownloadError';
		this.status = status;
	}
}

// Safely decode URL-encoded S3 keys (e.g. %2F -> /). Falls back to replacing
// %2F/%2f (case-insensitive) if decodeURIComponent fails on malformed input.
export const safeDecode = (s) => {
	if (!s || typeof s !== 'string') return s;
	try {
		return decodeURIComponent(s);
	} catch (e) {
		return s.replace(/%2F/gi, '/');
	}
};

export async function downloadFile(url, filename, onProgress) {
	if (!url) {
		throw new DownloadError('No file available to download.', 0);
	}

	const res = await fetch(url);
	if (!res.ok) {
		const notFound = res.status === 403 || res.status === 404;
		throw new DownloadError(notFound ? 'File not found.' : 'Download failed.', res.status);
	}

	const total = Number(res.headers.get('Content-Length')) || 0;
	const reader = res.body.getReader();
	const chunks = [];
	let loaded = 0;

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
		loaded += value.length;
		if (onProgress && total) onProgress(Math.floor((loaded / total) * 100));
	}

	const objectUrl = URL.createObjectURL(new Blob(chunks, { type: res.headers.get('Content-Type') || '' }));
	const link = document.createElement('a');
	link.href = objectUrl;
	link.download = filename || 'file';
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(objectUrl);
}
